import { useState } from "react";
import { apiFetch, ApiError } from "../api/client";
import { generateSshKeyPair, downloadPrivateKey } from "../lib/sshKeygen";
import { ReportTypeSelect } from "../components/ReportTypeSelect";

interface FormState {
  client_id: string; client_name: string; legal_name: string;
  report_types: string[]; reporting_entity_number: string;
  submitting_reporting_entity_number: string; reporting_entity_contact_id: string;
  activity_sector_code: string;
  oauth_client_id: string; oauth_client_secret: string;
  sftp_username: string; sftp_public_key: string; sftp_allowed_cidrs: string;
  portal_admin_email: string;
}

const EMPTY: FormState = {
  client_id: "", client_name: "", legal_name: "",
  report_types: [], reporting_entity_number: "",
  submitting_reporting_entity_number: "", reporting_entity_contact_id: "",
  activity_sector_code: "",
  oauth_client_id: "", oauth_client_secret: "",
  sftp_username: "", sftp_public_key: "", sftp_allowed_cidrs: "",
  portal_admin_email: "",
};

/* Loose on purpose: fn_admin_client is the authority (it also rejects
   0.0.0.0/0, which would open SFTP to the internet on an endpoint that has
   real Elastic IPs). This only catches the obvious typo before a round trip. */
const CIDR_RE = /^(\d{1,3}\.){3}\d{1,3}\/\d{1,2}$|^[0-9a-fA-F:]+\/\d{1,3}$/;

function splitCidrs(value: string): string[] {
  return value.split(",").map((c) => c.trim()).filter(Boolean);
}

/* report_types is the one non-text field (a multi-select), so it validates
   separately from the plain inputs below. */
type TextField = Exclude<keyof FormState, "report_types">;

/* Everything fn_portal_onboard_client treats as mandatory, plus the two
   report-critical fields the wizard used to omit. activity_sector_code is the
   only optional field on the form -- it defaults to 11 (MSB) server-side. */
const REQUIRED: TextField[] = [
  "client_id", "client_name", "legal_name",
  "reporting_entity_number", "submitting_reporting_entity_number",
  "reporting_entity_contact_id", "oauth_client_id", "oauth_client_secret",
  "sftp_username", "sftp_public_key", "portal_admin_email",
];

const DEFAULT_ACTIVITY_SECTOR_CODE = 11;
const EMAIL_RE = /^[^@\s]+@[^@\s]+\.[^@\s]+$/;

interface OnboardResult { client_id: string; onboarded: boolean; api_enabled: boolean; }

type FieldErrors = Partial<Record<keyof FormState, string>>;

function validate(form: FormState): FieldErrors {
  const errors: FieldErrors = {};
  for (const key of REQUIRED) {
    if (!form[key].trim()) errors[key] = "Required";
  }
  if (form.report_types.length === 0) errors.report_types = "Select at least one report type";
  if (!errors.portal_admin_email && !EMAIL_RE.test(form.portal_admin_email.trim())) {
    errors.portal_admin_email = "Enter a valid email address";
  }
  const badCidrs = splitCidrs(form.sftp_allowed_cidrs).filter((c) => !CIDR_RE.test(c));
  if (badCidrs.length) {
    errors.sftp_allowed_cidrs = `Not valid CIDR notation: ${badCidrs.join(", ")}`;
  }
  return errors;
}

export function OnboardPage() {
  const [form, setForm] = useState<FormState>(EMPTY);
  const [errors, setErrors] = useState<FieldErrors>({});
  const [error, setError] = useState<{ message: string; step?: string } | null>(null);
  const [result, setResult] = useState<OnboardResult | null>(null);
  const [submitting, setSubmitting] = useState(false);
  const [privateKey, setPrivateKey] = useState<string | null>(null);

  const clearError = (key: keyof FormState) =>
    setErrors((prev) => (prev[key] ? { ...prev, [key]: undefined } : prev));

  const set = (key: TextField) => (e: React.ChangeEvent<HTMLInputElement>) => {
    setForm((f) => ({ ...f, [key]: e.target.value }));
    clearError(key);
  };

  const setReportTypes = (report_types: string[]) => {
    setForm((f) => ({ ...f, report_types }));
    if (report_types.length) clearError("report_types");
  };

  const generateKeys = () => {
    const pair = generateSshKeyPair();
    setForm((f) => ({ ...f, sftp_public_key: pair.publicKey }));
    clearError("sftp_public_key");
    setPrivateKey(pair.privateKey);
  };

  const submit = async () => {
    const found = validate(form);
    setErrors(found);
    if (Object.keys(found).length > 0) {
      setError({ message: "Fill in the required fields before submitting." });
      return;
    }
    setSubmitting(true);
    setError(null);
    try {
      const sector = form.activity_sector_code.trim();
      const body = {
        client_id: form.client_id.trim(),
        client_name: form.client_name.trim(),
        legal_name: form.legal_name.trim(),
        report_types: form.report_types,
        reporting_entity_number: form.reporting_entity_number.trim(),
        submitting_reporting_entity_number: form.submitting_reporting_entity_number.trim(),
        reporting_entity_contact_id: form.reporting_entity_contact_id.trim(),
        activity_sector_code: sector ? Number(sector) : DEFAULT_ACTIVITY_SECTOR_CODE,
        oauth_client_id: form.oauth_client_id.trim(),
        oauth_client_secret: form.oauth_client_secret,
        sftp_username: form.sftp_username.trim(),
        sftp_public_key: form.sftp_public_key.trim(),
        sftp_allowed_cidrs: splitCidrs(form.sftp_allowed_cidrs),
        portal_admin_email: form.portal_admin_email.trim(),
      };
      const res = await apiFetch<OnboardResult>("/portal/customers", { method: "POST", body: JSON.stringify(body) });
      setResult(res);
    } catch (e) {
      if (e instanceof ApiError) {
        const body = e.body as { error?: string; step?: string };
        setError({ message: body.error ?? "onboarding failed", step: body.step });
      } else {
        setError({ message: "onboarding failed" });
      }
    } finally {
      setSubmitting(false);
    }
  };

  const field = (
    key: TextField,
    label: string,
    opts: { type?: string; hint?: string; placeholder?: string; full?: boolean } = {},
  ) => (
    <div className={`form-row${opts.full ? " full" : ""}`}>
      <label htmlFor={key}>
        {label}
        {REQUIRED.includes(key) && <span className="req" aria-hidden="true"> *</span>}
      </label>
      <input
        id={key}
        type={opts.type ?? "text"}
        placeholder={opts.placeholder}
        value={form[key]}
        onChange={set(key)}
        aria-invalid={errors[key] ? true : undefined}
      />
      {opts.hint && <span className="hint">{opts.hint}</span>}
      {errors[key] && <span className="field-error">{errors[key]}</span>}
    </div>
  );

  if (result) {
    return <div className="card"><p>Client {result.client_id} onboarded successfully.</p></div>;
  }

  return (
    <div className="card form-card">
      <h2>Onboard a new customer</h2>

      <div className="form">
        <section className="form-section">
          <h3 className="form-section-title">Client</h3>
          {field("client_id", "Client ID", { hint: "Lowercase letters, digits, - and _ (2-50 chars)." })}
          {field("client_name", "Client name")}
          {field("legal_name", "Legal name")}
        </section>

        <section className="form-section">
          <h3 className="form-section-title">FINTRAC reporting</h3>
          <div className="form-row full">
            <label htmlFor="report_types">
              Report types<span className="req" aria-hidden="true"> *</span>
            </label>
            <ReportTypeSelect
              id="report_types"
              value={form.report_types}
              onChange={setReportTypes}
              invalid={!!errors.report_types}
            />
            <span className="hint">Which reports the pipeline generates for this client.</span>
            {errors.report_types && <span className="field-error">{errors.report_types}</span>}
          </div>
          {field("reporting_entity_number", "Reporting entity number")}
          {field("submitting_reporting_entity_number", "Submitting reporting entity number", {
            hint: "The entity that submits to FINTRAC. Often the same as above.",
          })}
          {field("reporting_entity_contact_id", "Reporting entity contact ID", {
            hint: "Required on every report -- reports cannot be generated without it.",
          })}
          {field("activity_sector_code", "Activity sector code", {
            type: "number", placeholder: String(DEFAULT_ACTIVITY_SECTOR_CODE),
            hint: `Optional. Defaults to ${DEFAULT_ACTIVITY_SECTOR_CODE} (MSB).`,
          })}
          {field("oauth_client_id", "OAuth client ID", { hint: "FINTRAC F2R API credentials." })}
          {field("oauth_client_secret", "OAuth client secret", { type: "password" })}
        </section>

        <section className="form-section">
          <h3 className="form-section-title">SFTP</h3>
          {field("sftp_username", "SFTP username")}
          {field("sftp_public_key", "SFTP public key", {
            full: true,
            placeholder: "ssh-ed25519 AAAA... (paste the customer's own key, or generate one)",
          })}
          {field("sftp_allowed_cidrs", "Allowed source IPs (CIDR, comma-separated)", {
            full: true,
            placeholder: "203.0.113.0/24, 198.51.100.7/32",
            hint: "The customer's egress IPs. Leave empty and they can authenticate but "
                + "every connection is blocked at the firewall. Applied immediately -- no deploy needed.",
          })}
          <div className="form-row keygen full">
            <div className="keygen-actions">
              <button type="button" onClick={generateKeys}>Generate Ed25519 key pair</button>
              {privateKey && (
                <button type="button" onClick={() => downloadPrivateKey(privateKey)}>
                  Download private key
                </button>
              )}
            </div>
            {privateKey && (
              <span className="hint">
                Generated in your browser. The private key is never sent to the server -- download
                it now and hand it to the customer over a secure channel; it cannot be recovered later.
              </span>
            )}
          </div>
        </section>

        <section className="form-section">
          <h3 className="form-section-title">Portal admin</h3>
          {field("portal_admin_email", "Customer Admin email", {
            type: "email",
            hint: "Gets the Customer Admin account and the invite email. Without it nobody can log in.",
          })}
        </section>

        {error && (
          <p className="form-error">
            {error.message}{error.step ? ` (step: ${error.step})` : ""}
          </p>
        )}

        <div className="form-actions">
          <button className="btn-primary" onClick={submit} disabled={submitting}>
            {submitting ? "Submitting..." : "Submit"}
          </button>
        </div>
      </div>
    </div>
  );
}
